﻿import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import CryptoJS from 'crypto-js';

// customParseFormat：支援 parseIsoDate 使用嚴格 ISO 8601 解析
dayjs.extend(customParseFormat);

export default class helper {
    /**
     * 檢查物件是否為空值
     * @param obj
     */
    isEmpty(obj: object) {
        return obj
            && Object.keys(obj).length === 0
            && Object.getPrototypeOf(obj) === Object.prototype;
    }

    /**
     * 格式化字串
     * @param {string} [format] - 字串格式
     * @param {any} [args] - 取代參數
     */
    format(format: string, ...args: any[]) {
        if (!format) return '';
        let formatted = format;
        for (let i = 0; i < args.length; i++) {
            let regexp = new RegExp('\\{' + i + '\\}', 'gi');
            formatted = formatted.replace(regexp, args[i]);
        }
        return formatted;
    }

    /**
    * 格式化日期
    */
    formatDate(date: dayjs.ConfigType, format: string = 'YYYY-MM-DD HH:mm:ss'): string {
        const d = dayjs(date);
        return d.isValid() ? d.format(format) : '';
    }

    /**
     * 格式化民國日期
     * @param {dayjs.ConfigType} [date] - Date object
     * @param {string} [format] - 日期格式 ex: YYY/MM/DD HH:mm:ss
     */
    formatRocDate(date: dayjs.ConfigType, format: string = 'YYY/MM/DD HH:mm:ss'): string {
        const f = format.replace('YYY', '');
        const d = dayjs(date);
        if (!d.isValid()) return '';

        // 將民國年補足 3 位數 (例如 115, 098)
        const rocYear = String(d.year() - 1911).padStart(3, '0');
        return rocYear + this.formatDate(d.toDate(), f);
    }

    /**
     * 解析民國日期
     * @param {string} [rocDate] - 民國日期字串，前三碼為民國年
     */
    parseRocDate(rocDate: string): Date | undefined {
        if (!rocDate) return undefined;
        if (rocDate.length < 3) return undefined;
        // 將民國年轉換為西元年後再交給 dayjs 解析
        const westernDateStr = String(Number(rocDate.substring(0, 3)) + 1911) + rocDate.substring(3);
        const d = dayjs(westernDateStr);
        if (!d.isValid()) {
            return undefined;
        }
        return d.toDate();
    }

    /**
    * 解析日期 (支援 ISO 字串、Unix 秒數、Unix 毫秒)
    * @param {dayjs.ConfigType} [date] 
    */
    parseDate(date: dayjs.ConfigType): Date {
        if (!date) return new Date();

        // 處理 JWT 常見的 Unix Timestamp (秒)
        // 判斷邏輯：如果是數字且小於 100 億，通常是秒 (2026年約為 17 億)
        if (typeof date === 'number' && date < 10000000000) {
            return dayjs.unix(date).toDate();
        }

        return dayjs(date).toDate();
    }

    /**
     * 取得日期（時間歸零至當天 00:00:00）
     * @param {dayjs.ConfigType} [date] - Date object
     */
    getDate(date: dayjs.ConfigType): Date {
        // startOf('day') 等同於 moment startOf('day')，將時間歸零
        return dayjs(date).startOf('day').toDate();
    }

    /**
     * 為 Date 加上指定的月數
     * @param {Date} [date] - Date object
     * @param {number} [months] - 加上的月數值
     */
    addMonths(date: Date, months: number): Date {
        return dayjs(date).add(months, 'month').toDate();
    }

    /**
     * 為 Date 加上指定的天數
     * @param {Date} [date] - Date object
     * @param {number} [days] - 加上的天數值
     */
    addDays(date: Date, days: number): Date {
        return dayjs(date).add(days, 'day').toDate();
    }

    /**
     * 為 Date 加上指定的時數
     * @param {Date} [date] - Date object
     * @param {number} [hours] - 加上的時數值
     */
    addHours(date: Date, hours: number): Date {
        return dayjs(date).add(hours, 'hour').toDate();
    }

    /**
     * 為 date 加上指定的分鐘數
     * @param {Date} [date] - Date object
     * @param {number} [minutes] - 加上的分鐘數值
     */
    addMinutes(date: Date, minutes: number): Date {
        return dayjs(date).add(minutes, 'minute').toDate();
    }

    /**
     * 為 date 加上指定的秒數
     * @param {Date} [date] - Date object
     * @param {number} [seconds] - 加上的秒數值
     */
    addSeconds(date: Date, seconds: number): Date {
        return dayjs(date).add(seconds, 'second').toDate();
    }

    /**
    * 轉換 ISO 8601 字串為 Date object
    * @param {string | number} [isoDate] - ISO 8601 字串或時間戳
    * @param {boolean} [forceUtc] - 是否強制視為 UTC (當後端沒傳 Z 時)
    */
    parseIsoDate(isoDate: string | number, forceUtc: boolean = true): Date {
        if (typeof isoDate === 'string' && forceUtc && !isoDate.endsWith('Z') && !isoDate.includes('+')) {
            // 如果沒有時區資訊，手動補上 Z 確保 dayjs 把它當 UTC 處理
            return dayjs(isoDate + 'Z').toDate();
        }
        return dayjs(isoDate).toDate();
    }

    /**
    * 檢查日期是否已過期 (大於現在時間)
    * @param {dayjs.ConfigType} [targetDate] 
    */
    isExpired(targetDate: dayjs.ConfigType): boolean {
        const d = dayjs(targetDate);
        if (!d.isValid()) return true;
        return dayjs().isAfter(d);
    }

    /**
     * 轉換 Date 為 ISO 8601 字串（含時區 offset）
     * @param {dayjs.ConfigType} [d] - Date Object
     */
    toIsoDate(d: dayjs.ConfigType): string {
        // dayjs 預設 toISOString() 輸出 UTC 時間（同 moment toISOString(false)）
        // moment toISOString(true) 會保留本地時區 offset
        // 若需要保留本地時區，可改用：
        //   const date = dayjs(d).toDate();
        //   const offset = -date.getTimezoneOffset();
        //   const sign = offset >= 0 ? '+' : '-';
        //   ... 手動組出帶 offset 的字串
        // 目前行為與 moment(d).toISOString(false) 相同（輸出 UTC）
        return dayjs(d).toISOString();
    }

    /**
     * 將整數當做秒數轉換成 hh:mm:ss 字串格式
     */
    formatSeconds(seconds: number): string {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        const formattedHours = String(hours).padStart(2, '0');
        const formattedMinutes = String(minutes).padStart(2, '0');
        const formattedSeconds = String(secs).padStart(2, '0');
        return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    }

    /**
     * 秒數 → 時鐘字串(純數字、跨語系,不含任何語系文字)。
     * <para>顯示時長的統一入口(通話 elapsed / 排程 elapsed 等「時鐘型」時長)。</para>
     * <list type="bullet">
     *   <item>&gt;= 1 小時 → "HH:mm:ss"(時、分、秒皆補零,如 01:05:30)</item>
     *   <item>&lt; 1 小時  → "mm:ss"(分、秒補零,如 05:30)</item>
     *   <item>負數 / NaN / 0 → "00:00"</item>
     * </list>
     * 需顯示「天」者請改用 splitDuration(天數單位走呼叫端 i18n)。
     * @param totalSeconds 總秒數
     */
    formatClock(totalSeconds: number): string {
        if (!Number.isFinite(totalSeconds) || totalSeconds < 0) totalSeconds = 0;
        const total = Math.floor(totalSeconds);
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;
        const mm = String(m).padStart(2, '0');
        const ss = String(s).padStart(2, '0');
        if (h > 0) {
            const hh = String(h).padStart(2, '0');
            return `${hh}:${mm}:${ss}`;
        }
        return `${mm}:${ss}`;
    }

    /**
     * 秒數 → { days, clock }。供需要顯示「天」的時長(uptime / 跨天倒數)。
     * <para>days 為天數(整數);clock 為「當天剩餘」的 formatClock 結果(故 &lt; 1 小時為 mm:ss、否則 HH:mm:ss)。
     * 「天」的單位文字由呼叫端以 i18n 接(本方法不含任何語系文字,確保多語系)。</para>
     * @param totalSeconds 總秒數
     * @returns days:天數;clock:當天剩餘時鐘字串
     */
    splitDuration(totalSeconds: number): { days: number; clock: string } {
        if (!Number.isFinite(totalSeconds) || totalSeconds < 0) totalSeconds = 0;
        const total = Math.floor(totalSeconds);
        const days = Math.floor(total / 86400);
        const rest = total - days * 86400;
        return { days, clock: this.formatClock(rest) };
    }

    /**
     * 格式化百分比
     */
    formatPercentage(num: number) {
        return new Intl.NumberFormat('default', {
            style: 'percent',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(num);
    }

    /**
     * 為一個 url 加入 parameter
     */
    addParameterToUrl(url: string | URL, paramName: string, paramValue: string) {
        const parsedUrl = new URL(url);
        const searchParams = new URLSearchParams(parsedUrl.search.slice(1));
        searchParams.set(paramName, paramValue);
        parsedUrl.search = '?' + searchParams.toString();
        return parsedUrl.href;
    }

    /**
     * 為一個 url 加入 [open_in_external_browser=1] 參數
     */
    addOpenInExternalBrowser(url: string | URL) {
        return this.addParameterToUrl(url, 'open_in_external_browser', '1');
    }

    /** 產生 uuid 字串 */
    get uuid(): string {
        return crypto.randomUUID();
    }

    /**
     * 轉全形字元
     */
    toDBC(txtstring: string) {
        let dbc = '';
        for (let i = 0; i < txtstring.length; i++) {
            if (txtstring.charCodeAt(i) == 32) {
                dbc = dbc + String.fromCharCode(12288);
            } else if (txtstring.charCodeAt(i) < 127) {
                dbc = dbc + String.fromCharCode(txtstring.charCodeAt(i) + 65248);
            } else {
                dbc = dbc + String.fromCharCode(txtstring.charCodeAt(i));
            }
        }
        return dbc;
    }

    /**
     * 轉半形字元
     */
    toCDB(txtstring: string) {
        let cdb = '';
        for (let i = 0; i < txtstring.length; i++) {
            if (txtstring.charCodeAt(i) == 12288) {
                cdb += String.fromCharCode(txtstring.charCodeAt(i) - 12256);
                continue;
            }
            if (txtstring.charCodeAt(i) > 65280 && txtstring.charCodeAt(i) < 65375) {
                cdb += String.fromCharCode(txtstring.charCodeAt(i) - 65248);
            } else {
                cdb += String.fromCharCode(txtstring.charCodeAt(i));
            }
        }
        return cdb;
    }

    /**
     * 深層複製
     */
    deepCopy(source: object): any {
        return structuredClone(source);
    }

    /**
     * 取得副檔名
     */
    getFileExt(filename: string): string {
        const r = /[^.]+$/.exec(filename) as RegExpExecArray;
        if (r.length > 0) {
            return r[0];
        }
        return '';
    }

    /**
     * 取得檔案名稱
     */
    getFileName(filePath: string): string {
        const parts = filePath.split(/[\\/]/);
        return parts.pop() ?? '';
    }

    /**
     * 取得不含副檔名的檔案名稱
     */
    getFilenameWithoutExt(filename: string): string {
        return filename.split('.').slice(0, -1).join('.');
    }

    /**
     * Format bytes as human-readable text.
     * @param bytes Number of bytes.
     * @param si True to use metric (SI) units (powers of 1000). False to use binary (IEC) (powers of 1024).
     * @param dp Number of decimal places to display.
     */
    humanFileSize(bytes: number, si: boolean = true, dp: number = 1) {
        const thresh = si ? 1000 : 1024;
        if (Math.abs(bytes) < thresh) {
            return bytes + ' B';
        }
        const units = si
            ? ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
            : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
        let u = -1;
        const r = 10 ** dp;
        do {
            bytes /= thresh;
            ++u;
        } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);
        return bytes.toFixed(dp) + ' ' + units[u];
    }

    /**
     * 加密 Storage 值（production 環境使用 AES 加密，其餘環境直接 JSON 序列化）
     */
    encryptStore(state: any, keyString: string): string {
        if (import.meta.env.MODE === 'production') {
            // AES.encrypt 回傳 CipherParams 物件而非字串（crypto-js cipher-core.js SerializableCipher.encrypt）；
            // persist serializer 契約要求回傳 string，故顯式 toString() 取 Base64 密文，不倚賴 storage 端隱式轉字串。
            return CryptoJS.AES.encrypt(JSON.stringify(state), keyString).toString();
        } else {
            return JSON.stringify(state);
        }
    }

    /**
     * 解密 Storage 值
     */
    decryptStore(value: string, keyString: string): any {
        if (import.meta.env.MODE === 'production') {
            return JSON.parse(CryptoJS.AES.decrypt(value, keyString).toString(CryptoJS.enc.Utf8));
        } else {
            return JSON.parse(value);
        }
    }

    /**
     * 設定 cookie
     */
    setCookie(name: string, value: string, daysToExpire?: number) {
        let expires = '';
        if (daysToExpire) {
            const date = new Date();
            date.setTime(date.getTime() + (daysToExpire * 24 * 60 * 60 * 1000));
            expires = '; expires=' + date.toUTCString();
        }
        document.cookie = name + '=' + value + expires + '; path=/; SameSite=Strict';
    }

    /**
     * 取得 cookie
     */
    getCookie(name: string) {
        const nameEQ = name + '=';
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            let cookie = cookies[i];
            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1, cookie.length);
            }
            if (cookie.indexOf(nameEQ) === 0) {
                return cookie.substring(nameEQ.length, cookie.length);
            }
        }
        return undefined;
    }

    /**
     * 移除 cookie
     */
    deleteCookie(name: string) {
        this.setCookie(name, '', -1);
    }

    /**
     * 等待指定毫秒數
     */
    sleep(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * 將 C# DateTime 的 UTC ticks 轉為 JS Date。
     * <para>C# ticks = 自 0001-01-01 起的 100 奈秒數;JS Date = 自 1970-01-01 起的毫秒。
     * 換算:先減去 epoch 差 (621355968000000000 ticks = 1970-01-01),再除以 10000 轉毫秒。</para>
     * @param ticks UTC ticks(後端 DateTime.ToUniversalTime().Ticks)
     * @returns 對應的 Date;ticks 為 0/null/undefined 時回傳 null
     */
    ticksToDate(ticks: number | null | undefined): Date | null {
        if (ticks == null || ticks <= 0) return null;
        // 621355968000000000 = DateTime(1970,1,1).Ticks;10000 ticks = 1ms
        const ms = (ticks - 621355968000000000) / 10000;
        return new Date(ms);
    }

    /**
     * 將 C# DateTime 的 UTC ticks 轉為本地時區格式化字串。
     * @param ticks UTC ticks
     * @param format dayjs 格式(預設 'YYYY-MM-DD HH:mm:ss')
     * @returns 格式化字串;ticks 無效時回傳空字串
     */
    ticksToLocalString(ticks: number | null | undefined, format: string = 'YYYY-MM-DD HH:mm:ss'): string {
        const d = this.ticksToDate(ticks);
        return d ? this.formatDate(d, format) : '';
    }
}